import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { settingsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert } from '@/components/common/Ui'

/**
 * Settings > Email: send one test email with the saved mail account.
 *
 * Uses what is saved, not what is typed — the alert does the same — so the
 * box says to save first while there are unsaved changes. Sent to the
 * Company Email unless another address is given.
 */
export default function EmailTest({ canEdit, unsaved }) {
  const [to, setTo] = useState('')
  const [result, setResult] = useState(null)

  const test = useMutation({
    mutationFn: () => settingsApi.testEmail(to.trim()),
    onSuccess: (answer) => setResult({ tone: answer.ok ? 'success' : 'danger', text: answer.detail }),
    onError: (e) => setResult({ tone: 'danger', text: errorMessage(e) }),
  })

  if (!canEdit) return null
  return (
    <section className="panel settings-row">
      <div className="settings-label">
        <span className="settings-title">Test email</span>
      </div>
      <p className="hint">
        Sends one email with the saved settings above — to the Company Email (Company tab), or
        to the address below. Alerts, such as the AI service running out of credit, are sent the
        same way.
      </p>
      <Alert tone={result?.tone ?? 'success'} message={result?.text} onClose={() => setResult(null)} />
      <div className="settings-control email-test">
        <input
          type="email"
          placeholder="Company Email (leave blank)"
          aria-label="Send the test to"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button
          type="button"
          className="btn"
          disabled={test.isPending || unsaved}
          title={unsaved ? 'Save your changes first' : undefined}
          onClick={() => {
            setResult(null)
            test.mutate()
          }}
        >
          {test.isPending ? 'Sending…' : 'Send test email'}
        </button>
      </div>
      {unsaved && <p className="hint">Save your changes first — the test uses the saved settings.</p>}
    </section>
  )
}
